import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Sparkles, Flame, ChevronRight } from "lucide-react";

const BAND_STYLES = {
  critical: { bg: "bg-[#FEF2F2]",  border: "border-[#DC2626]/30", ring: "bg-[#DC2626]", dot: "text-[#DC2626]" },
  high:     { bg: "bg-[#FEF7EC]",  border: "border-[#F59E0B]/35", ring: "bg-[#F59E0B]", dot: "text-[#B45309]" },
  followup: { bg: "bg-[#FBF6EA]",  border: "border-[#C5A880]/45", ring: "bg-[#C5A880]", dot: "text-[#7A5B14]" },
  info:     { bg: "bg-[#ECFDF5]",  border: "border-[#059669]/30", ring: "bg-[#059669]", dot: "text-[#065F46]" },
};

export default function PrioritiesWidget({ lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/recommendations/summary");
        setData(r.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return null;
  if ((data.items || []).length === 0) {
    return (
      <div className="rounded-xl border border-[#E4E1D8] bg-white p-6 shadow-sm" data-testid="dashboard-priorities-empty">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-[#C5A880]" strokeWidth={1.8} />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
            {lang === "fr" ? "Actions prioritaires" : "Priority actions"}
          </div>
        </div>
        <div className="text-sm text-[#5B5B62]">
          {lang === "fr" ? "Complétez votre diagnostic pour révéler vos priorités." : "Complete your diagnostic to reveal your priorities."}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-priorities" className="rounded-xl border border-[#E4E1D8] bg-white p-6 md:p-7 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#C5A880]" strokeWidth={1.8} />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
              {lang === "fr" ? "Actions prioritaires" : "Priority actions"}
            </div>
          </div>
          <div className="mt-1 font-display text-lg text-[#1F2937] tracking-tight">
            {lang === "fr" ? "Vos 3 leviers les plus rentables aujourd'hui" : "Your 3 highest-yield levers today"}
          </div>
        </div>
        <Link to="/app/priorities" className="inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#123163] font-mono uppercase tracking-widest">
          {lang === "fr" ? "Tout voir" : "See all"}
          <ArrowUpRight className="h-3 w-3" strokeWidth={1.8} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(data.items || []).map((rec) => {
          const s = BAND_STYLES[rec.band] || BAND_STYLES.info;
          return (
            <Link
              key={rec.rec_key}
              to={rec.deep_link || "/app/priorities"}
              data-testid={`dashboard-priority-${rec.rec_key}`}
              className={`group relative rounded-lg border ${s.border} ${s.bg} p-4 altiora-transition hover:-translate-y-0.5 hover:shadow-md`}
            >
              <div className="absolute left-0 top-3 bottom-3 w-0.5 rounded-r-full" style={{ background: s.ring.includes("#") ? undefined : "" }}></div>
              <div className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-r-full ${s.ring}`}></div>
              <div className="pl-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-base leading-none">{rec.emoji}</span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest ${s.dot}`}>
                    {rec.label}
                  </span>
                </div>
                <div className="mt-2 font-display text-sm text-[#1F2937] leading-snug line-clamp-2">
                  {rec.title}
                </div>
                <div className="mt-2 text-[11px] text-[#5B5B62] leading-relaxed line-clamp-2">
                  {rec.raw_message}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                    {lang === "fr" ? "Impact" : "Impact"} <span className="text-[#1F2937] font-medium">{Math.round(rec.impact_score)}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] opacity-70 group-hover:opacity-100 altiora-transition">
                    {lang === "fr" ? "Agir" : "Act"}
                    <ChevronRight className="h-3 w-3" strokeWidth={2} />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
        <Flame className="h-3 w-3 text-[#DC2626]" strokeWidth={2} />
        {data.counts?.critical || 0} · {lang === "fr" ? "critique" : "critical"} ·
        <span className="text-[#B45309]">🟠</span> {data.counts?.high || 0} ·
        <span className="text-[#7A5B14]">🟡</span> {data.counts?.followup || 0} ·
        <span className="text-[#065F46]">🟢</span> {data.counts?.info || 0}
      </div>
    </div>
  );
}
